/**
 * V36TopNav — Sticky pill glass nav (v3.6)
 * 핸드오프: docs/handoff/video-editor/project/v3_6/v3-6-shell.jsx
 *
 * 5 nav items: 홈 / AI 생성 / 씬 에디터 / 숏폼 / 내 작업 (lib)
 * - PC: 4탭 노출 + 검색/벨/아바타
 * - 모바일: 햄버거 → V36MobileDrawer 풀스크린 모달
 */
'use client'

import Link from 'next/link'
import { usePathname, useRouter } from 'next/navigation'
import { useState } from 'react'
import { ClipflowLogo } from '@/components/brand/ClipflowLogo'
import { V3Icon, type V3IconName } from '@/components/icons/V3Icon'
import { useAuthStore } from '@/store/authStore'

interface NavItem {
  id: string
  label: string
  icon: V3IconName
  href: string
  /** PC 4탭에서 제외 (lib 같은 것) */
  drawerOnly?: boolean
}

const NAV_ITEMS: NavItem[] = [
  { id: 'home', label: '홈', icon: 'home', href: '/' },
  { id: 'ai', label: 'AI 생성', icon: 'sparkle', href: '/ai' },
  { id: 'scene', label: '씬 에디터', icon: 'film', href: '/projects' },
  { id: 'short', label: '숏폼', icon: 'scissors', href: '/shortform' },
  { id: 'wuxia', label: '무협', icon: 'sparkle', href: '/wuxia' },
  { id: 'lib', label: '내 작업', icon: 'folder', href: '/projects', drawerOnly: true },
]

function isActive(href: string, pathname: string): boolean {
  if (href === '/') return pathname === '/'
  return pathname === href || pathname.startsWith(href + '/')
}

export default function V36TopNav() {
  const pathname = usePathname() ?? '/'
  const router = useRouter()
  const { isAuthenticated, user, logout } = useAuthStore()
  const [drawerOpen, setDrawerOpen] = useState(false)
  const [profileOpen, setProfileOpen] = useState(false)

  const handleLogout = () => {
    logout()
    setProfileOpen(false)
    router.push('/')
  }

  return (
    <>
      <header
        style={{
          position: 'sticky',
          top: 12,
          zIndex: 50,
          padding: '0 12px',
          marginTop: 12,
        }}
      >
        <div
          className="v36-glass-strong"
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 12,
            padding: '8px 8px 8px 16px',
            borderRadius: 999,
            boxShadow: '0 8px 24px rgba(0,0,0,0.4)',
          }}
        >
          {/* 모바일 햄버거 */}
          <button
            className="v36-mobile-only v36-btn v36-btn--ghost"
            onClick={() => setDrawerOpen(true)}
            aria-label="메뉴 열기"
            style={{
              width: 36,
              height: 36,
              padding: 0,
              borderRadius: '50%',
              marginLeft: -8,
            }}
          >
            <V3Icon name="menu" size={18} />
          </button>

          {/* 로고 */}
          <Link href="/" aria-label="Clipflow 홈" style={{ display: 'inline-flex', alignItems: 'center' }}>
            <ClipflowLogo height={26} priority />
          </Link>

          {/* 데스크톱 4탭 */}
          <nav
            className="v36-pc-only"
            style={{ display: 'flex', gap: 2, marginLeft: 16 }}
          >
            {NAV_ITEMS.filter((it) => !it.drawerOnly).map((it) => {
              const active = isActive(it.href, pathname)
              return (
                <Link
                  key={it.id}
                  href={it.href}
                  className="v36-tab"
                  style={{
                    background: active ? 'var(--v36-ink)' : 'transparent',
                    color: active ? 'var(--v36-bg)' : 'var(--v36-ink-3)',
                  }}
                >
                  <V3Icon name={it.icon} size={16} />
                  <span>{it.label}</span>
                </Link>
              )
            })}
          </nav>

          {/* 우측 액션 */}
          <div
            style={{
              marginLeft: 'auto',
              display: 'flex',
              alignItems: 'center',
              gap: 6,
            }}
          >
            <button
              className="v36-btn v36-btn--ghost v36-pc-only"
              aria-label="검색"
              style={{ width: 36, height: 36, padding: 0, borderRadius: '50%' }}
            >
              <V3Icon name="search" size={16} />
            </button>
            <button
              className="v36-btn v36-btn--ghost"
              aria-label="알림"
              style={{ width: 36, height: 36, padding: 0, borderRadius: '50%' }}
            >
              <V3Icon name="bell" size={16} />
            </button>

            {isAuthenticated && user ? (
              <div style={{ position: 'relative' }}>
                <button
                  onClick={() => setProfileOpen((v) => !v)}
                  aria-label="프로필 메뉴"
                  style={{
                    width: 32,
                    height: 32,
                    borderRadius: '50%',
                    background: 'linear-gradient(135deg, #FF6FA8, #FFB020)',
                    color: '#fff',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: 12,
                    fontWeight: 600,
                    border: 0,
                    cursor: 'pointer',
                  }}
                >
                  {user.name?.charAt(0)?.toUpperCase() || 'U'}
                </button>

                {profileOpen && (
                  <>
                    <div
                      onClick={() => setProfileOpen(false)}
                      style={{ position: 'fixed', inset: 0, zIndex: 40 }}
                    />
                    <div
                      className="v36-glass-strong v36-fade-in"
                      style={{
                        position: 'absolute',
                        right: 0,
                        top: 'calc(100% + 8px)',
                        zIndex: 50,
                        width: 220,
                        padding: 8,
                        borderRadius: 14,
                        boxShadow: '0 24px 48px rgba(0,0,0,0.5)',
                      }}
                    >
                      <div style={{ padding: '8px 12px', borderBottom: '1px solid var(--v36-line)' }}>
                        <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--v36-ink)' }}>
                          {user.name}
                        </div>
                        <div className="v36-mono" style={{ fontSize: 10, color: 'var(--v36-ink-4)', marginTop: 2 }}>
                          {user.email}
                        </div>
                      </div>
                      {[
                        { label: '내 프로젝트', href: '/projects' },
                        { label: '프로필 설정', href: '/profile' },
                        { label: '구독 관리', href: '/profile/subscription' },
                      ].map((m) => (
                        <Link
                          key={m.href}
                          href={m.href}
                          onClick={() => setProfileOpen(false)}
                          style={{
                            display: 'block',
                            padding: '10px 12px',
                            borderRadius: 8,
                            fontSize: 13,
                            color: 'var(--v36-ink-2)',
                          }}
                        >
                          {m.label}
                        </Link>
                      ))}
                      <div style={{ height: 1, background: 'var(--v36-line)', margin: '4px 0' }} />
                      <button
                        onClick={handleLogout}
                        style={{
                          width: '100%',
                          textAlign: 'left',
                          padding: '10px 12px',
                          borderRadius: 8,
                          fontSize: 13,
                          color: 'var(--v36-error)',
                        }}
                      >
                        로그아웃
                      </button>
                    </div>
                  </>
                )}
              </div>
            ) : (
              <Link
                href="/login"
                className="v36-btn v36-btn--neon v36-btn--sm v36-pc-only"
                style={{ marginLeft: 4 }}
              >
                <V3Icon name="bolt" size={13} />
                <span>시작하기</span>
              </Link>
            )}
          </div>
        </div>
      </header>

      <V36MobileDrawer open={drawerOpen} onClose={() => setDrawerOpen(false)} pathname={pathname} />
    </>
  )
}

/* ════════════════════════════════════════════════════════════ */
/* V36MobileDrawer — 풀스크린 글래스 모달                        */
/* ════════════════════════════════════════════════════════════ */
function V36MobileDrawer({
  open,
  onClose,
  pathname,
}: {
  open: boolean
  onClose: () => void
  pathname: string
}) {
  const { isAuthenticated, user } = useAuthStore()
  if (!open) return null

  return (
    <div
      className="v36-mobile-only"
      onClick={onClose}
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 100,
        background: 'rgba(10,10,12,0.7)',
        backdropFilter: 'blur(12px)',
        WebkitBackdropFilter: 'blur(12px)',
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: 'var(--v36-bg-2)',
          margin: 12,
          marginTop: 12,
          borderRadius: 24,
          padding: 20,
          boxShadow: '0 24px 64px rgba(0,0,0,0.6)',
          border: '1px solid rgba(255,255,255,0.10)',
        }}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            marginBottom: 24,
          }}
        >
          <ClipflowLogo height={24} />
          <button
            className="v36-btn v36-btn--ghost"
            onClick={onClose}
            aria-label="닫기"
            style={{ width: 36, height: 36, padding: 0, borderRadius: '50%' }}
          >
            <V3Icon name="x" size={18} />
          </button>
        </div>

        <nav style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          {NAV_ITEMS.map((it) => {
            const active = isActive(it.href, pathname)
            return (
              <Link
                key={it.id}
                href={it.href}
                onClick={onClose}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 14,
                  padding: '14px 14px',
                  borderRadius: 12,
                  background: active ? 'var(--v36-ink)' : 'transparent',
                  color: active ? 'var(--v36-bg)' : 'var(--v36-ink)',
                  fontSize: 16,
                  fontWeight: 500,
                }}
              >
                <V3Icon name={it.icon} size={20} />
                <span>{it.label}</span>
                {active && <V3Icon name="check" size={16} style={{ marginLeft: 'auto' }} />}
              </Link>
            )
          })}

          {!isAuthenticated && (
            <>
              <div style={{ height: 1, background: 'var(--v36-line)', margin: '12px 0 4px' }} />
              <Link
                href="/login"
                onClick={onClose}
                style={{
                  padding: '14px 14px',
                  borderRadius: 12,
                  fontSize: 16,
                  color: 'var(--v36-ink-2)',
                }}
              >
                로그인
              </Link>
              <Link
                href="/signup"
                onClick={onClose}
                className="v36-btn v36-btn--neon"
                style={{ height: 48, marginTop: 4 }}
              >
                <V3Icon name="bolt" size={14} />
                <span>무료로 시작하기</span>
              </Link>
            </>
          )}

          {isAuthenticated && user && (
            <>
              <div style={{ height: 1, background: 'var(--v36-line)', margin: '12px 0 4px' }} />
              <div style={{ padding: '8px 14px' }}>
                <div className="v36-label" style={{ fontSize: 9, marginBottom: 4 }}>
                  로그인됨
                </div>
                <div style={{ fontSize: 14, fontWeight: 600 }}>{user.name}</div>
                <div className="v36-mono" style={{ fontSize: 10, color: 'var(--v36-ink-4)', marginTop: 2 }}>
                  {user.email}
                </div>
              </div>
            </>
          )}
        </nav>
      </div>
    </div>
  )
}
